import {
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';

import useService from '@/lib/hooks/useService';
import type { CommunityData } from '@/types/database/communityData';

import {
  checkQuestionIsExistInDatabase,
  defaultMemberInfoQuestions,
  defaultQuestions,
  defaultSignUpQuestions,
  getChineseOrderId,
  getColumns,
  getOrderIdOptions,
  isRequiredOptions,
  sortQuestions,
  validateIsNameDuplicate,
} from '../helper';
import type { ExtraUserInfoFormat, MemberInfoRule } from '../types';
import styles from './index.module.scss';

const { Title } = Typography;
const { Item, useForm } = Form;
const { TextArea } = Input;

const CollectionSetting = () => {
  const [questionForm] = useForm<ExtraUserInfoFormat>();
  const [editRuleForm] = useForm<MemberInfoRule>();

  const [communityData, updateCommunityData, communityDataService] =
    useService<CommunityData>({
      collectionName: 'communityData',
    });
  const [customQuestions, setCustomQuestions] = useState<
    (ExtraUserInfoFormat & { category: string })[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [formType, setFormType] = useState<'add' | 'edit'>('add');
  const [isNameError, setIsNameError] = useState(false);
  const [isEditRule, setIsEditRule] = useState(false);

  useEffect(() => {
    if (communityData.length !== 0) {
      const customQuestions = communityData[0].extraUserInfoFormat.reduce(
        (
          questions: (ExtraUserInfoFormat & { category: string })[],
          question
        ) => {
          if (
            question.isDeleted === false &&
            defaultQuestions.includes(question.name) === false
          ) {
            questions.push({ ...question, category: '自訂' });
            return questions;
          }
          return questions;
        },
        []
      );
      setCustomQuestions(customQuestions);
    }
  }, [communityData]);

  useEffect(() => {
    if (communityData.length !== 0 && editRuleForm) {
      editRuleForm.setFieldValue(
        'communityRule',
        communityData[0].communityRule.content
      );
    }
  }, [communityData, editRuleForm]);

  const memberInfoQuestions = defaultMemberInfoQuestions
    .concat(customQuestions)
    .sort(sortQuestions);

  const addQuestionToDatabase = async (
    extraUserInfoFormatId: { $oid: string },
    extraUserInfoFormat: ExtraUserInfoFormat[]
  ) => {
    try {
      await communityDataService.patch(extraUserInfoFormatId, {
        extraUserInfoFormat,
      });
      updateCommunityData();
      closeAddQuestionModal();
      message.success('新增問題成功!');
    } catch (error) {
      message.error('新增問題失敗!');
    }
  };

  const restoreExistedQuestion = async (args: {
    extraUserInfoFormatId: { $oid: string };
    formValues: ExtraUserInfoFormat;
    allCustomQuestions: ExtraUserInfoFormat[];
  }) => {
    const { extraUserInfoFormatId, formValues, allCustomQuestions } = args;

    if (isNameError) {
      setIsNameError(false);
    }

    const restoredQuestion = {
      ...formValues,
      orderId: formValues.orderId - 1,
      isDefault: false,
      isDeleted: false,
    };
    const newExtraUserInfoFormat = allCustomQuestions.map((question) => {
      if (question.name === formValues.name) {
        return restoredQuestion;
      }
      if (
        question.orderId >= formValues.orderId - 1 &&
        question.orderId !== allCustomQuestions.length + 4
      ) {
        return {
          ...question,
          orderId: question.orderId + 1,
        };
      }
      return question;
    });

    await addQuestionToDatabase(extraUserInfoFormatId, newExtraUserInfoFormat);
  };

  const createNewQuestion = async (args: {
    extraUserInfoFormatId: { $oid: string };
    formValues: ExtraUserInfoFormat;
    allCustomQuestions: ExtraUserInfoFormat[];
  }) => {
    const { extraUserInfoFormatId, formValues, allCustomQuestions } = args;

    if (isNameError) {
      setIsNameError(false);
    }

    const sortedQuestions = allCustomQuestions.map((question) => {
      if (question.orderId >= formValues.orderId - 1) {
        return {
          ...question,
          orderId: question.orderId + 1,
        };
      }
      return question;
    });
    const insertIndex = formValues.orderId - 6;

    sortedQuestions.splice(insertIndex, 0, {
      ...formValues,
      orderId: formValues.orderId - 1,
      isDefault: false,
      isDeleted: false,
    });

    await addQuestionToDatabase(extraUserInfoFormatId, sortedQuestions);
  };

  const handleAddQuestionFormSubmit = async (
    formValues: ExtraUserInfoFormat
  ) => {
    const allCustomQuestions = communityData[0].extraUserInfoFormat;
    const extraUserInfoFormatId = communityData[0]._id;
    const isNameDuplicate = validateIsNameDuplicate(
      allCustomQuestions,
      formValues.name
    );
    const isExisted = checkQuestionIsExistInDatabase(
      allCustomQuestions,
      formValues.name
    );

    if (isNameDuplicate) {
      setIsNameError(true);
    }

    if (isNameDuplicate === false && isExisted) {
      await restoreExistedQuestion({
        extraUserInfoFormatId,
        formValues,
        allCustomQuestions,
      });
    }

    if (isNameDuplicate === false && isExisted === false) {
      await createNewQuestion({
        extraUserInfoFormatId,
        formValues,
        allCustomQuestions,
      });
    }
  };

  const openEditQuestionModal = () => {
    setFormType('edit');
    setIsOpenModal(true);
  };

  const handleEditQuestionFormSubmit = async () => {
    setIsSubmitting(true);

    const allFormValues = questionForm.getFieldsValue(true);

    const { category: _category, ...values } = allFormValues;

    const updateData = communityData[0].extraUserInfoFormat.map(
      (questionObject) => {
        if (questionObject.name === values.name) {
          return values;
        }

        return questionObject;
      }
    );

    try {
      const communityDataId = communityData[0]._id;
      await communityDataService.patch(communityDataId, {
        extraUserInfoFormat: updateData,
      });
      setIsSubmitting(false);
      updateCommunityData();
      setIsOpenModal(false);
      message.success('編輯問題成功!');
    } catch (error) {
      message.error('編輯問題失敗!');
    }
  };

  const closeAddQuestionModal = () => {
    setIsNameError(false);
    setIsOpenModal(false);
  };

  const handleEditRuleFormSubmit = async (formValue: MemberInfoRule) => {
    const data = {
      communityRule: {
        content: formValue.communityRule,
        updatedTime: dayjs().valueOf(),
      },
    };

    try {
      await communityDataService.patch(communityData[0]._id, data);
      setIsEditRule(false);
      updateCommunityData();
      message.success('申明條款與使用條約修改成功!');
    } catch (error) {
      message.error('申明條款與使用條約修改失敗!');
    }
  };

  const memberInfoColumns = useMemo(() => {
    if (communityData.length !== 0) {
      const columns = getColumns({
        tableType: 'infoCollection',
        extraUserInfoFormat: communityData[0].extraUserInfoFormat,
        communityDataId: communityData[0]._id,
        updateCommunityData,
        form: questionForm,
        openEditQuestionModal,
      });
      return columns;
    }

    return [];
  }, [communityData, questionForm, updateCommunityData]);

  const openAddQuestionModal = () => {
    if (formType === 'edit') {
      questionForm.resetFields();
    }
    setFormType('add');
    setIsOpenModal(true);
  };

  return (
    <Space direction="vertical" className={styles.widthFull} size={40}>
      <Card>
        <Title level={5}>申明條款與使用條約</Title>
        <Form
          form={editRuleForm}
          layout="vertical"
          onFinish={handleEditRuleFormSubmit}
        >
          <Item name="communityRule">
            <TextArea
              autoSize
              bordered={isEditRule}
              placeholder="請輸入申明條款與使用條約內容"
              value={editRuleForm.getFieldValue('communityRule')}
              disabled={isEditRule === false}
            />
          </Item>
        </Form>
        <div className={styles.footerButtonsContainer}>
          {isEditRule ? (
            <Space>
              <Button onClick={() => setIsEditRule(false)}>取消</Button>
              <Button type="primary" onClick={() => editRuleForm.submit()}>
                儲存
              </Button>
            </Space>
          ) : (
            <Button onClick={() => setIsEditRule(true)}>編輯</Button>
          )}
        </div>
      </Card>
      <Card>
        <Title level={5}>預設會員註冊問題</Title>
        <Table
          rowKey="orderId"
          bordered
          pagination={{ hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          columns={getColumns({ tableType: 'memberSignUp' })}
          dataSource={defaultSignUpQuestions}
        />
      </Card>
      <Space direction="vertical" className={styles.widthFull}>
        <Button
          className={styles.addQuestionButton}
          onClick={openAddQuestionModal}
        >
          新增問題
        </Button>
        <Card>
          <Title level={5}>會員個資蒐集問題</Title>
          <Table
            rowKey="orderId"
            bordered
            pagination={{ hideOnSinglePage: true }}
            scroll={{ x: 'max-content' }}
            columns={memberInfoColumns}
            dataSource={memberInfoQuestions}
          />
        </Card>
      </Space>
      <Modal
        open={isOpenModal}
        centered
        footer={null}
        destroyOnClose
        keyboard={false}
        maskClosable={false}
        title={formType === 'add' ? '新增問題' : '編輯問題'}
        closable={!isSubmitting}
        onCancel={closeAddQuestionModal}
      >
        <Form
          form={questionForm}
          layout="vertical"
          preserve={false}
          onFinish={
            formType === 'add'
              ? handleAddQuestionFormSubmit
              : handleEditQuestionFormSubmit
          }
        >
          {formType === 'add' ? (
            <>
              <Item
                label="問題"
                name="name"
                rules={[{ required: true }]}
                validateStatus={isNameError ? 'error' : 'success'}
                help={isNameError ? '名稱重複' : null}
              >
                <Input placeholder="請輸入問題" className={styles.widthFull} />
              </Item>
              <Item
                label="順序"
                name="orderId"
                rules={[{ required: true, message: '請選擇順序' }]}
              >
                <Select
                  placeholder="請選擇順序"
                  className={styles.widthFull}
                  options={getOrderIdOptions(customQuestions)}
                />
              </Item>
            </>
          ) : (
            <>
              <Item>
                <h5 className={styles.editFormItemTitle}>編號</h5>
                <p>
                  {getChineseOrderId(questionForm.getFieldValue('orderId'))}
                </p>
              </Item>
              <Item>
                <h5 className={styles.editFormItemTitle}>問題</h5>
                <p>{questionForm.getFieldValue('name')}</p>
              </Item>
            </>
          )}
          <Item
            label="選/必填"
            name="isRequired"
            rules={[{ required: true, message: '請選擇選/必填' }]}
          >
            <Select
              placeholder="請選擇選/必填"
              className={styles.widthFull}
              options={isRequiredOptions}
            />
          </Item>
          <Item>
            <Space className={styles.footerButtonsContainer}>
              <Button
                onClick={
                  formType === 'add'
                    ? closeAddQuestionModal
                    : () => setIsOpenModal(false)
                }
                disabled={isSubmitting}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                確定
              </Button>
            </Space>
          </Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default CollectionSetting;
